import { AppUnauthorized } from '@/app';
import { AppDashboardTypeSetter } from '@/app/AppDashboardTypeSetter';
import { AppHubspotListener } from '@/app/AppHubspotListener';
import { AppMaintenance } from '@/app/AppMaintenance';
import { Redirect } from '@/app/Redirect';
import { CompleteSignIn } from '@/auth';
import { AuthProtected } from '@/auth/components';
import { Integrations } from '@/client/accounts/types/account-user-management.type';
import { MaterialType } from '@/client/courses';
import { SCViewEnum } from '@/client/security-culture/types';
import { Actions, Subjects, SystemRoles } from '@/client/users';
import {
  AppRoutesPaths,
  RedirectPaths,
  RedirectPathsEnum,
} from '@/common/constants';
import { SecurityProfileOnboardingGuard } from '@/components/security-profile/onboarding';
import { useAppSelector } from '@/hooks/store';
import { useFeatureFlag } from '@/hooks/useFeatureFlag';
import { usePermission } from '@/hooks/usePermission';
import { MainLayout } from '@/layout';
import {
  AccountCoursesPage,
  AccountDataControlPage,
  AccountDomainVerificationPage,
  AccountEmailControlPage,
  AccountErrorsPage,
  AccountGoPhish,
  AccountHubspot,
  AccountInternalPage,
  AccountSecurityProfilePage,
  AccountsPage,
  AccountTrainingSettingsPage,
  CreateUpdateAccountPage,
} from '@/pages/accounts';
import { AccountAPIPage } from '@/pages/accounts/AccountAPIPage';
import { AccountDocumentsPage } from '@/pages/accounts/AccountDocumentsPage';
import { AccountFeaturesPage } from '@/pages/accounts/AccountFeaturesPage';
import { AccountGooglePage } from '@/pages/accounts/AccountGooglePage';
import { AccountIntegrationsPage } from '@/pages/accounts/AccountIntegrationsPage';
import { AccountMicrosoftPage } from '@/pages/accounts/AccountMicrosoftPage';
import { AdminTasksPage } from '@/pages/admin-tasks';
import {
  BranchesImport,
  BranchesPage,
  BranchUsers,
  CreateBranchPage,
  UpdateBranchPage,
} from '@/pages/branches';
import {
  CampaignsCatalogueListPage,
  CampaignsCataloguePageV2,
  CampaignsPage,
  CampaignsReportPage,
  PhishingAnalyticsPage,
  UpdateCampaignPage,
} from '@/pages/campaigns';
import {
  CampaignMasterTemplateFormPage,
  CampaignMasterTemplatesPage,
} from '@/pages/campaigns/master-templates';
import { PhishingGlobalSettingsPage } from '@/pages/campaigns/PhishingGlobalSettingsPage';
import { PhishingSimulationsPage } from '@/pages/campaigns/PhishingSimulationsPage';
import {
  AccountCourseSchedulePage,
  AWTAnalyticsPage,
  CourseAccountsPage,
  CourseBranchesPage,
  CourseContentPage,
  CourseGroupsPage,
  CoursesAnalytics,
  CourseSchedulePage,
  CoursesPage,
  CourseTranslatePage,
  CourseUsersPage,
  CreateCoursePage,
} from '@/pages/courses';
import { AccountTrainingPlannerPage } from '@/pages/courses/AccountTrainingPlannerPage';
import { ContentPreviewPage } from '@/pages/courses/ContentPreviewPage';
import { CourseChangesPage } from '@/pages/courses/CourseChangesPage';
import { DocumentViewPage } from '@/pages/courses/CourseDocumentViewPage';
import { CoursesResultsPage } from '@/pages/courses/CoursesResultsPage';
import { CustomizeCoursePage } from '@/pages/courses/CustomizeCoursePage';
import { ReplaceScheduledContentPage } from '@/pages/courses/ReplaceScheduledContentPage';
import {
  LearnerDashboardActivePage,
  LearnerDashboardCompletedPage,
} from '@/pages/dashboards';
import { AdminDashboardPageV2 } from '@/pages/dashboards/admin/AdminDashboardPageV2';
import {
  LearnerPhishingDetailPage,
  LearnerPhishingOverviewPage,
} from '@/pages/dashboards/learner/phishing';
import {
  EmailTemplatesCreatePage,
  EmailTemplatesCustomPage,
  EmailTemplatesPage,
  EmailTemplatesUpdatePage,
} from '@/pages/email-templates';
import { FeaturesPage } from '@/pages/features';
import { FindUserPage } from '@/pages/find-user';
import {
  CreateGroupPage,
  GroupsPage,
  GroupUsers,
  UpdateGroupPage,
} from '@/pages/groups';
import { HubspotBookingPage } from '@/pages/hubspot-booking';
import { HubspotSyncPage } from '@/pages/hubspot-sync';
import {
  LandingPageTemplatesCreatePage,
  LandingPageTemplatesCustomPage,
  LandingPageTemplatesPage,
  LandingPageTemplatesUpdatePage,
} from '@/pages/landing-page-templates';
import { LoginPage } from '@/pages/login';
import LogoutPage from '@/pages/LogoutPage';
import { ProfileSettingsPage } from '@/pages/me/ProfileSettingsPage';
import { NotFoundPage } from '@/pages/not-found';
import {
  RedirectPageTemplatesCreatePage,
  RedirectPageTemplatesCustomPage,
  RedirectPageTemplatesPage,
  RedirectPageTemplatesUpdatePage,
} from '@/pages/redirect-page-templates';
import { AwarenessReportsPage, SummaryReportsPage } from '@/pages/reports';
import {
  AccountsToAResellerPage,
  CreateResellersPage,
  ResellersPage,
  SystemResellerPage,
  UpdateResellerPage,
} from '@/pages/resellers';
import {
  MaterialCreatePage,
  MaterialEditPage,
  MaterialsPage,
  OwnMaterialsTransitionPage,
  PostersPage,
} from '@/pages/resources';
import {
  SCPlannerPage,
  SecurityCulturePage,
  SecurityCultureSendSurveyPage,
  SecurityCultureSendSurveyPageV2,
  SecurityCultureUploadSurveyPage,
} from '@/pages/security-culture';
import { SCDashboardV2 } from '@/pages/security-culture/sc-dashboard-filterable';
import { SurveyPage } from '@/pages/security-culture/SurveyPage';
import { TakeSurveyPage } from '@/pages/security-culture/TakeSurveyPage';
import {
  CreateSenderProfilePage,
  SenderProfilesCustomPage,
  SenderProfilesPage,
  UpdateSenderProfilePage,
} from '@/pages/sender-profiles';
import {
  CreateUserPage,
  UpdateUserPage,
  UserCoursesPage,
  UserPhishingPage,
  UserPropertiesPage,
  UsersPage,
} from '@/pages/users';
import { ScormCourseInfoPage } from '@/scorm';
import { ContentPlayerPage } from '@/scorm/pages/ContentPlayerPage';
import { selectCurrentAccount } from '@/store/features/account';
import { selectCurrentUser } from '@/store/features/users';
import {
  SystemEmailsCreateTypeConfigPage,
  SystemEmailsSendingConfigPage,
  SystemEmailsTemplatesCreatePage,
  SystemEmailsTemplatesUpdatePage,
  SystemEmailsTypesConfigsPage,
  SystemEmailsUpdateTypeConfigPage,
  SystemEmailTemplatesPage,
} from '@/system-settings';
import { branchAdminCheck, getAccountIntegration } from '@/utils/helpers';
import { PostHogFeatureFlags } from '@/utils/posthog/posthog.events';
import React from 'react';
import { Navigate, Route, Routes, useParams } from 'react-router-dom';

const UserDocumentsRedirect = () => {
  const { id } = useParams();
  return (
    <Navigate
      to={RedirectPaths[RedirectPathsEnum.USERS_COURSES](id ?? '')}
      replace
    />
  );
};

const CustomMaterialEditRedirect = () => {
  const { id } = useParams();
  return (
    <Navigate
      to={RedirectPaths[RedirectPathsEnum.COURSES_CONTENT](id ?? '')}
      replace
    />
  );
};

const AppRouter = () => {
  const { can, cannot } = usePermission();
  const { canUseFeature, accountCanUseFeature } = useFeatureFlag();
  const currentUser = useAppSelector(selectCurrentUser);
  const currentAccount = useAppSelector(selectCurrentAccount);

  const isBranchAdmin = branchAdminCheck(currentUser, currentAccount);

  if (process.env.REACT_APP_MAINTENANCE_MODE) {
    return (
      <Routes>
        <Route path="*" element={<AppMaintenance />} />
      </Routes>
    );
  }

  const canAccessIntegrations =
    can(Actions.READ, Subjects.AD_SYNC) &&
    can(Actions.READ, Subjects.ACCOUNT_SSO) &&
    !isBranchAdmin;

  const currentAccountIntegrationSource =
    getAccountIntegration(currentAccount)?.source;

  return (
    <Routes>
      <Route path={AppRoutesPaths.BASE} element={<AuthProtected />}>
        <Route path={AppRoutesPaths.BASE} element={<AppHubspotListener />}>
          <Route path={AppRoutesPaths.BASE} element={<Redirect />} />
          <Route
            path={AppRoutesPaths.BASE}
            element={<SecurityProfileOnboardingGuard />}
          >
            <Route path={AppRoutesPaths.BASE} element={<MainLayout />}>
              {/* Admin Dashboard Routes */}
              <Route element={<AppDashboardTypeSetter isAdmin={true} />}>
                {can(Actions.READ, Subjects.HUBSPOT_ACCOUNT_LINK) && (
                  <Route
                    path={AppRoutesPaths.HUBSPOT_SYNC_ROUTE}
                    element={<HubspotSyncPage />}
                  />
                )}
                <Route
                  path={AppRoutesPaths.HUBSPOT_BOOKING_ROUTE}
                  element={<HubspotBookingPage />}
                />
                {can(Actions.READ, Subjects.USERS) &&
                  currentUser?.account.isSystem && (
                    <Route
                      path={AppRoutesPaths.FIND_USER_ROUTE}
                      element={<FindUserPage />}
                    />
                  )}
                <>
                  {can(Actions.READ, Subjects.SYSTEM_SETTINGS) && (
                    <>
                      <Route
                        path={AppRoutesPaths.SYSTEM_EMAILS_TEMPLATES_ROUTE}
                        element={<SystemEmailTemplatesPage />}
                      />
                      <Route
                        path={
                          AppRoutesPaths.SYSTEM_EMAILS_SENDING_CONFIGS_ROUTE
                        }
                        element={<SystemEmailsSendingConfigPage />}
                      />
                      <Route
                        path={AppRoutesPaths.SYSTEM_EMAILS_TYPES_CONFIGS_ROUTE}
                        element={<SystemEmailsTypesConfigsPage />}
                      />
                    </>
                  )}
                  <Route
                    path={AppRoutesPaths.SECURITY_CULTURE_SURVEY_ROUTE}
                    element={<SCDashboardV2 view={SCViewEnum.ALL} />}
                  />
                  {accountCanUseFeature(
                    PostHogFeatureFlags.SIMPLIFIED_SC_SURVEY_PLANNER,
                    currentAccount?.featureFlagAbility ?? [],
                  ) &&
                    !currentAccount?.isSystem && (
                      <>
                        <Route
                          path={
                            AppRoutesPaths.SECURITY_CULTURE_SURVEY_PLANNER_ROUTE
                          }
                          element={<SCPlannerPage />}
                        />
                        <Route
                          path={
                            AppRoutesPaths.SECURITY_CULTURE_SEND_SURVEY_V2_ROUTE
                          }
                          element={<SecurityCultureSendSurveyPageV2 />}
                        />
                      </>
                    )}
                  {can(Actions.READ, Subjects.SECURITY_CULTURE) && (
                    <>
                      <Route
                        path={AppRoutesPaths.SECURITY_CULTURE_FEEDBACK_ROUTE}
                        element={<SCDashboardV2 view={SCViewEnum.FEEDBACK} />}
                      />
                      <Route
                        path={AppRoutesPaths.SECURITY_CULTURE_CATEGORY_ROUTE}
                        element={<SCDashboardV2 view={SCViewEnum.CATEGORY} />}
                      />
                      <Route
                        path={AppRoutesPaths.SECURITY_CULTURE_EDIT_SURVEY_ROUTE}
                        element={<SecurityCultureUploadSurveyPage />}
                      />
                    </>
                  )}
                  {can(Actions.CREATE, Subjects.SECURITY_CULTURE) && (
                    <>
                      <Route
                        path={AppRoutesPaths.SURVEY_ROUTE}
                        element={<SurveyPage />}
                      />
                      <Route
                        path={AppRoutesPaths.SECURITY_CULTURE_ROUTE}
                        element={<SecurityCulturePage />}
                      />
                      <Route
                        path={
                          AppRoutesPaths.SECURITY_CULTURE_UPLOAD_SURVEY_ROUTE
                        }
                        element={<SecurityCultureUploadSurveyPage />}
                      />

                      {currentUser?.account.isSystem &&
                        !accountCanUseFeature(
                          PostHogFeatureFlags.SIMPLIFIED_SC_SURVEY_PLANNER,
                          currentAccount?.featureFlagAbility ?? [],
                        ) && (
                          <Route
                            path={
                              AppRoutesPaths.SECURITY_CULTURE_SEND_SURVEY_ROUTE
                            }
                            element={<SecurityCultureSendSurveyPage />}
                          />
                        )}
                    </>
                  )}
                  {can(Actions.CREATE, Subjects.SYSTEM_SETTINGS) && (
                    <>
                      <Route
                        path={
                          AppRoutesPaths.SYSTEM_EMAILS_TEMPLATES_CREATE_ROUTE
                        }
                        element={<SystemEmailsTemplatesCreatePage />}
                      />
                      <Route
                        path={
                          AppRoutesPaths.SYSTEM_EMAILS_TYPES_CONFIGS_CREATE_ROUTE
                        }
                        element={<SystemEmailsCreateTypeConfigPage />}
                      />
                    </>
                  )}

                  {can(Actions.UPDATE, Subjects.SYSTEM_SETTINGS) && (
                    <>
                      <Route
                        path={AppRoutesPaths.SYSTEM_EMAILS_TEMPLATES_EDIT_ROUTE}
                        element={<SystemEmailsTemplatesUpdatePage />}
                      />
                      <Route
                        path={
                          AppRoutesPaths.SYSTEM_EMAILS_TYPES_CONFIGS_EDIT_ROUTE
                        }
                        element={<SystemEmailsUpdateTypeConfigPage />}
                      />
                    </>
                  )}
                </>
                {can(Actions.READ, Subjects.USERS) && (
                  <>
                    {/* Users Routes */}
                    {can(Actions.READ, Subjects.USERS) && (
                      <Route
                        path={AppRoutesPaths.USERS_ROUTE}
                        element={<UsersPage />}
                      />
                    )}
                    {can(Actions.CREATE, Subjects.USERS) &&
                      (!currentAccount?.isSystem ||
                        [
                          SystemRoles.DEVELOPER,
                          SystemRoles.SUPER_ADMIN,
                        ].includes(currentUser?.role?.code as SystemRoles)) && (
                        <Route
                          path={AppRoutesPaths.USERS_CREATE_ROUTE}
                          element={<CreateUserPage />}
                        />
                      )}
                    {can(Actions.UPDATE, Subjects.USERS) && (
                      <Route
                        path={AppRoutesPaths.USERS_EDIT_ROUTE}
                        element={<UpdateUserPage />}
                      />
                    )}
                    {can(Actions.READ, Subjects.USERS) && (
                      <>
                        <Route
                          path={AppRoutesPaths.USERS_COURSES_ROUTE}
                          element={<UserCoursesPage />}
                        />
                        <Route
                          path={AppRoutesPaths.USERS_DOCUMENTS_ROUTE}
                          element={<UserDocumentsRedirect />}
                        />
                        <Route
                          path={AppRoutesPaths.USERS_PROPERTIES_ROUTE}
                          element={<UserPropertiesPage />}
                        />
                        <Route
                          path={AppRoutesPaths.USERS_PHISHING_ROUTE}
                          element={<UserPhishingPage />}
                        />
                      </>
                    )}

                    {/* Branch Routes */}
                    {can(Actions.READ, Subjects.BRANCHES) && (
                      <Route
                        path={AppRoutesPaths.BRANCHES_ROUTE}
                        element={<BranchesPage />}
                      />
                    )}
                    {can(Actions.CREATE, Subjects.BRANCHES) &&
                      (!currentAccount?.isSystem ||
                        currentUser?.role?.code === SystemRoles.DEVELOPER) && (
                        <Route
                          path={AppRoutesPaths.BRANCHES_CREATE_ROUTE}
                          element={<CreateBranchPage />}
                        />
                      )}
                    {can(Actions.UPDATE, Subjects.BRANCHES) && (
                      <Route
                        path={AppRoutesPaths.BRANCHES_EDIT_ROUTE}
                        element={<UpdateBranchPage />}
                      />
                    )}
                    {can(Actions.READ, Subjects.BRANCHES) && (
                      <Route
                        path={AppRoutesPaths.BRANCHES_USERS_ROUTE}
                        element={<BranchUsers />}
                      />
                    )}
                    {can(Actions.READ, Subjects.BRANCHES) && (
                      <Route
                        path={AppRoutesPaths.BRANCHES_USERS_IMPORT_ROUTE}
                        element={<BranchesImport />}
                      />
                    )}
                  </>
                )}
                {/* Resellers Routes */}
                {
                  <>
                    {can(Actions.READ, Subjects.RESELLERS) && (
                      <Route
                        path={AppRoutesPaths.RESELLERS_ROUTE}
                        element={<ResellersPage />}
                      />
                    )}
                    {can(Actions.CREATE, Subjects.RESELLERS) &&
                      currentUser?.role?.code === SystemRoles.DEVELOPER && (
                        <Route
                          path={AppRoutesPaths.RESELLERS_CREATE_ROUTE}
                          element={<CreateResellersPage />}
                        />
                      )}
                    {can(Actions.UPDATE, Subjects.RESELLERS) && (
                      <Route
                        path={AppRoutesPaths.RESELLERS_EDIT_ROUTE}
                        element={<UpdateResellerPage />}
                      />
                    )}
                    {can(Actions.READ, Subjects.RESELLERS) && (
                      <Route
                        path={AppRoutesPaths.RESELLERS_ACCOUNTS_ROUTE}
                        element={<AccountsToAResellerPage />}
                      />
                    )}
                    {can(Actions.UPDATE, Subjects.RESELLERS) && (
                      <Route
                        path={AppRoutesPaths.RESELLERS_SYSTEM_ROUTE}
                        element={<SystemResellerPage />}
                      />
                    )}
                  </>
                }
                {/* Accounts Routes */}
                {can(Actions.READ, Subjects.ACCOUNTS_SYSTEM_PAGE) &&
                  cannot(Actions.READ, Subjects.OWN_ACCOUNT) && (
                    <Route
                      path={AppRoutesPaths.ACCOUNTS_ROUTE}
                      element={<AccountsPage />}
                    />
                  )}
                {(can(Actions.READ, Subjects.ACCOUNTS) ||
                  can(Actions.READ, Subjects.OWN_ACCOUNT) ||
                  can(Actions.READ, Subjects.ASSIGNED_ACCOUNTS)) && (
                  <Route
                    path={AppRoutesPaths.ACCOUNT_ROUTE}
                    element={<CreateUpdateAccountPage />}
                  />
                )}
                {can(Actions.CREATE, Subjects.ACCOUNTS) && (
                  <Route
                    path={AppRoutesPaths.ACCOUNTS_CREATE_ROUTE}
                    element={<CreateUpdateAccountPage />}
                  />
                )}
                {can(Actions.UPDATE, Subjects.ACCOUNTS) && (
                  <>
                    <Route
                      path={AppRoutesPaths.ACCOUNTS_UPDATE_ROUTE}
                      element={<CreateUpdateAccountPage />}
                    />
                  </>
                )}
                {can(Actions.READ, Subjects.ACCOUNT_INTERNAL_TAB) && (
                  <>
                    <Route
                      path={AppRoutesPaths.ACCOUNTS_INTERNAL_ROUTE}
                      element={<AccountInternalPage />}
                    />
                    <Route
                      path={AppRoutesPaths.ACCOUNTS_INTERNAL_GO_PHISH_ROUTE}
                      element={<AccountGoPhish />}
                    />
                    <Route
                      path={AppRoutesPaths.ACCOUNTS_INTERNAL_HUBSPOT_ROUTE}
                      element={<AccountHubspot />}
                    />
                  </>
                )}

                {canAccessIntegrations && (
                  <>
                    <Route
                      path={AppRoutesPaths.ACCOUNT_INTEGRATIONS_ROUTE}
                      element={<AccountIntegrationsPage />}
                    />
                    <Route
                      path={AppRoutesPaths.ACCOUNTS_INTEGRATIONS_ROUTE}
                      element={<AccountIntegrationsPage />}
                    />
                    {(currentAccount?.isSystem ||
                      currentAccountIntegrationSource !==
                        Integrations.GOOGLE_WORKSPACE) && (
                      <>
                        <Route
                          path={
                            AppRoutesPaths.ACCOUNT_INTEGRATIONS_MICROSOFT_ROUTE
                          }
                          element={<AccountMicrosoftPage />}
                        />
                        <Route
                          path={
                            AppRoutesPaths.ACCOUNTS_INTEGRATIONS_MICROSOFT_ROUTE
                          }
                          element={<AccountMicrosoftPage />}
                        />
                      </>
                    )}
                    {(currentAccount?.isSystem ||
                      currentAccountIntegrationSource !==
                        Integrations.ACTIVE_DIRECTORY) && (
                      <>
                        <Route
                          path={
                            AppRoutesPaths.ACCOUNT_INTEGRATIONS_GOOGLE_ROUTE
                          }
                          element={<AccountGooglePage />}
                        />
                        <Route
                          path={
                            AppRoutesPaths.ACCOUNTS_INTEGRATIONS_GOOGLE_ROUTE
                          }
                          element={<AccountGooglePage />}
                        />
                      </>
                    )}
                  </>
                )}
                <Route
                  path={AppRoutesPaths.ACCOUNT_EMAILS_AND_DELIVERY_ROUTE}
                  element={<AccountEmailControlPage />}
                />
                <Route
                  path={AppRoutesPaths.ACCOUNTS_EMAILS_AND_DELIVERY_ROUTE}
                  element={<AccountEmailControlPage />}
                />

                <Route
                  path={AppRoutesPaths.ACCOUNT_DATA_CONTROL_ROUTE}
                  element={<AccountDataControlPage />}
                />
                <Route
                  path={AppRoutesPaths.ACCOUNTS_DATA_CONTROL_ROUTE}
                  element={<AccountDataControlPage />}
                />
                <Route
                  path={AppRoutesPaths.ACCOUNT_DOMAIN_VERIFICATION_ROUTE}
                  element={<AccountDomainVerificationPage />}
                />

                <Route
                  path={AppRoutesPaths.ACCOUNT_TRAINING_SETTINGS_ROUTE}
                  element={<AccountTrainingSettingsPage />}
                />
                <Route
                  path={AppRoutesPaths.ACCOUNTS_TRAINING_SETTINGS_ROUTE}
                  element={<AccountTrainingSettingsPage />}
                />

                {can(Actions.CREATE, Subjects.COURSE_ACCOUNT_AVAILABILITY) && (
                  <>
                    <Route
                      path={AppRoutesPaths.ACCOUNT_COURSES_ROUTE}
                      element={<AccountCoursesPage />}
                    />
                    <Route
                      path={AppRoutesPaths.ACCOUNT_SECURITY_DOCUMENTS_ROUTE}
                      element={<AccountDocumentsPage />}
                    />
                  </>
                )}
                {can(Actions.READ, Subjects.USERS) && (
                  <Route
                    path={AppRoutesPaths.ACCOUNT_ERRORS_ROUTE}
                    element={<AccountErrorsPage />}
                  />
                )}
                {(can(Actions.READ, Subjects.ACCOUNTS) ||
                  can(Actions.READ, Subjects.OWN_ACCOUNT)) && (
                  <>
                    <Route
                      path={AppRoutesPaths.ACCOUNT_SECURITY_PROFILE_ROUTE}
                      element={<AccountSecurityProfilePage />}
                    />
                    <Route
                      path={AppRoutesPaths.ACCOUNTS_SECURITY_PROFILE_ROUTE}
                      element={<AccountSecurityProfilePage />}
                    />
                  </>
                )}
                {can(Actions.READ, Subjects.ACCOUNT_FEATURE_FLAGS) && (
                  <Route
                    path={AppRoutesPaths.ACCOUNT_FEATURE_FLAGS_ROUTE}
                    element={<AccountFeaturesPage />}
                  />
                )}
                <Route
                  path={AppRoutesPaths.ACCOUNT_API_ROUTE}
                  element={<AccountAPIPage />}
                />
                <Route
                  path={AppRoutesPaths.ACCOUNTS_API_ROUTE}
                  element={<AccountAPIPage />}
                />
                {/* {can(Actions.READ, Subjects.ACCOUNTS) && (
              <Route
                path={AppRoutesPaths.ACCOUNT_SAML_CONFIG_ROUTE}
                element={<AccountsSAMLConfigPage />}
              />
            )} */}
                {/* Groups Routes */}

                <>
                  {can(Actions.READ, Subjects.GROUPS) && !isBranchAdmin && (
                    <Route
                      path={AppRoutesPaths.GROUPS_ROUTE}
                      element={<GroupsPage />}
                    />
                  )}
                  {can(Actions.CREATE, Subjects.GROUPS) &&
                    !isBranchAdmin &&
                    (!currentAccount?.isSystem ||
                      currentUser?.role?.code === SystemRoles.DEVELOPER) && (
                      <Route
                        path={AppRoutesPaths.GROUPS_CREATE_ROUTE}
                        element={<CreateGroupPage />}
                      />
                    )}
                  {can(Actions.UPDATE, Subjects.GROUPS) && !isBranchAdmin && (
                    <Route
                      path={AppRoutesPaths.GROUPS_EDIT_ROUTE}
                      element={<UpdateGroupPage />}
                    />
                  )}
                  {can(Actions.READ, Subjects.GROUPS) && !isBranchAdmin && (
                    <Route
                      path={AppRoutesPaths.GROUPS_USERS_ROUTE}
                      element={<GroupUsers />}
                    />
                  )}
                </>

                <>
                  {/* Campaign Routes */}
                  {can(Actions.READ, Subjects.CAMPAIGNS) && (
                    <Route
                      path={AppRoutesPaths.CAMPAIGNS_ROUTE}
                      element={<CampaignsPage />}
                    />
                  )}
                  <Route
                    path={AppRoutesPaths.PHISHING_ANALYTICS_ROUTE}
                    element={<PhishingAnalyticsPage />}
                  />
                  {can(Actions.READ, Subjects.CAMPAIGNS) &&
                    canUseFeature(Subjects.SELF_SERVICE_AUTO_PHISHING) && (
                      <Route
                        path={AppRoutesPaths.PHISHING_SIMULATIONS_ROUTE}
                        element={<PhishingSimulationsPage />}
                      />
                    )}
                  {can(Actions.READ, Subjects.CAMPAIGNS) && (
                    <>
                      <Route
                        path={AppRoutesPaths.CAMPAIGNS_CATALOGUE_ROUTE}
                        element={<CampaignsCatalogueListPage />}
                      />
                      <Route
                        path={AppRoutesPaths.CAMPAIGNS_CATALOGUE_V2_ROUTE}
                        element={<CampaignsCataloguePageV2 />}
                      />
                    </>
                  )}
                  {can(Actions.UPDATE, Subjects.CAMPAIGNS) && (
                    <Route
                      path={AppRoutesPaths.CAMPAIGNS_EDIT_ROUTE}
                      element={<UpdateCampaignPage />}
                    />
                  )}
                  {can(Actions.READ, Subjects.CAMPAIGN_RESULTS) && (
                    <Route
                      path={AppRoutesPaths.CAMPAIGNS_REPORT_ROUTE}
                      element={<CampaignsReportPage />}
                    />
                  )}
                  {/* Phishing Global Settings Routes */}
                  {can(Actions.READ, Subjects.SUPPRESSED_DOMAINS) && (
                    <Route
                      path={AppRoutesPaths.PHISHING_GLOBAL_SETTINGS_ROUTE}
                      element={<PhishingGlobalSettingsPage />}
                    />
                  )}
                  {/* Campaign Master Templates Routes */}
                  {can(Actions.READ, Subjects.SUPPRESSED_DOMAINS) && (
                    <>
                      <Route
                        path={AppRoutesPaths.CAMPAIGN_MASTER_TEMPLATES_ROUTE}
                        element={<CampaignMasterTemplatesPage />}
                      />
                      <Route
                        path={
                          AppRoutesPaths.CAMPAIGN_MASTER_TEMPLATES_CREATE_ROUTE
                        }
                        element={<CampaignMasterTemplateFormPage />}
                      />
                      <Route
                        path={
                          AppRoutesPaths.CAMPAIGN_MASTER_TEMPLATES_EDIT_ROUTE
                        }
                        element={<CampaignMasterTemplateFormPage />}
                      />
                    </>
                  )}
                  {/* Landing Page templates Routes */}
                  {can(Actions.READ, Subjects.PAGES) && (
                    <>
                      <Route
                        path={
                          AppRoutesPaths.LANDING_PAGE_TEMPLATES_PREDEFINED_ROUTE
                        }
                        element={<LandingPageTemplatesPage />}
                      />
                      <Route
                        path={
                          AppRoutesPaths.LANDING_PAGE_TEMPLATES_CUSTOM_ROUTE
                        }
                        element={<LandingPageTemplatesCustomPage />}
                      />
                    </>
                  )}
                  {can(Actions.CREATE, Subjects.PAGES) && (
                    <Route
                      path={AppRoutesPaths.LANDING_PAGE_TEMPLATES_CREATE_ROUTE}
                      element={<LandingPageTemplatesCreatePage />}
                    />
                  )}
                  {can(Actions.UPDATE, Subjects.PAGES) && (
                    <Route
                      path={AppRoutesPaths.LANDING_PAGE_TEMPLATES_UPDATE_ROUTE}
                      element={<LandingPageTemplatesUpdatePage />}
                    />
                  )}
                  {/* Redirect Page templates Routes */}
                  {can(Actions.READ, Subjects.PAGES) && (
                    <>
                      <Route
                        path={
                          AppRoutesPaths.REDIRECT_PAGE_TEMPLATES_PREDEFINED_ROUTE
                        }
                        element={<RedirectPageTemplatesPage />}
                      />
                      <Route
                        path={
                          AppRoutesPaths.REDIRECT_PAGE_TEMPLATES_CUSTOM_ROUTE
                        }
                        element={<RedirectPageTemplatesCustomPage />}
                      />
                    </>
                  )}
                  {can(Actions.CREATE, Subjects.PAGES) && (
                    <Route
                      path={AppRoutesPaths.REDIRECT_PAGE_TEMPLATES_CREATE_ROUTE}
                      element={<RedirectPageTemplatesCreatePage />}
                    />
                  )}
                  {can(Actions.UPDATE, Subjects.PAGES) && (
                    <Route
                      path={AppRoutesPaths.REDIRECT_PAGE_TEMPLATES_UPDATE_ROUTE}
                      element={<RedirectPageTemplatesUpdatePage />}
                    />
                  )}
                  {/* Email templates Routes */}
                  {can(Actions.READ, Subjects.EMAIL_TEMPLATES) && (
                    <>
                      <Route
                        path={AppRoutesPaths.EMAIL_TEMPLATES_PREDEFINED_ROUTE}
                        element={<EmailTemplatesPage />}
                      />
                      <Route
                        path={AppRoutesPaths.EMAIL_TEMPLATES_CUSTOM_ROUTE}
                        element={<EmailTemplatesCustomPage />}
                      />
                    </>
                  )}
                  {can(Actions.CREATE, Subjects.EMAIL_TEMPLATES) && (
                    <Route
                      path={AppRoutesPaths.EMAIL_TEMPLATES_CREATE_ROUTE}
                      element={<EmailTemplatesCreatePage />}
                    />
                  )}
                  {can(Actions.UPDATE, Subjects.EMAIL_TEMPLATES) && (
                    <Route
                      path={AppRoutesPaths.EMAIL_TEMPLATES_UPDATE_ROUTE}
                      element={<EmailTemplatesUpdatePage />}
                    />
                  )}
                  {/* Sender Profiles Routes */}
                  {can(Actions.READ, Subjects.SENDING_PROFILES) && (
                    <>
                      <Route
                        path={AppRoutesPaths.SENDER_PROFILES_PREDEFINED_ROUTE}
                        element={<SenderProfilesPage />}
                      />
                      <Route
                        path={AppRoutesPaths.SENDER_PROFILES_CUSTOM_ROUTE}
                        element={<SenderProfilesCustomPage />}
                      />
                    </>
                  )}
                  {can(Actions.CREATE, Subjects.SENDING_PROFILES) && (
                    <Route
                      path={AppRoutesPaths.SENDER_PROFILES_CREATE_ROUTE}
                      element={<CreateSenderProfilePage />}
                    />
                  )}
                  {can(Actions.UPDATE, Subjects.SENDING_PROFILES) && (
                    <Route
                      path={AppRoutesPaths.SENDER_PROFILES_EDIT_ROUTE}
                      element={<UpdateSenderProfilePage />}
                    />
                  )}
                </>
                {
                  <>
                    {/* Dashboard Routes */}
                    {can(Actions.READ, Subjects.ADMIN_DASHBOARD) && (
                      <Route
                        path={AppRoutesPaths.ADMIN_DASHBOARD_ROUTE}
                        element={<AdminDashboardPageV2 />}
                      />
                    )}

                    {currentAccount?.isSystem && (
                      <Route
                        path={AppRoutesPaths.ADMIN_TASKS_ROUTE}
                        element={<AdminTasksPage />}
                      />
                    )}

                    {/* User profile */}
                    {can(Actions.UPDATE, Subjects.ME) && (
                      <Route
                        path={AppRoutesPaths.PROFILE_SETTINGS_ROUTE}
                        element={<ProfileSettingsPage />}
                      />
                    )}

                    {/* Courses Routes */}
                    <Route
                      path={AppRoutesPaths.CONTENT_ANALYTICS_ROUTE}
                      element={<AWTAnalyticsPage />}
                    />
                    {can(Actions.READ, Subjects.COURSES) && (
                      <Route
                        path={AppRoutesPaths.COURSES_ROUTE}
                        element={<CoursesPage />}
                      />
                    )}
                    <Route
                      path={AppRoutesPaths.COURSES_CREATE_ROUTE}
                      element={<CreateCoursePage />}
                    />
                    {/* TODO: Add correct permissions */}
                    {can(Actions.READ, Subjects.COURSES) && (
                      <>
                        <Route
                          path={AppRoutesPaths.COURSES_CONTENT_ROUTE}
                          element={<CourseContentPage />}
                        />
                        <Route
                          path={AppRoutesPaths.COURSES_SCHEDULE_ROUTE}
                          element={<CourseSchedulePage />}
                        />
                        <Route
                          path={AppRoutesPaths.COURSES_ACCOUNTS_ROUTE}
                          element={
                            <CourseAccountsPage type={MaterialType.COURSE} />
                          }
                        />
                        <Route
                          path={AppRoutesPaths.COURSES_BRANCHES_ROUTE}
                          element={<CourseBranchesPage />}
                        />
                        <Route
                          path={AppRoutesPaths.COURSES_GROUPS_ROUTE}
                          element={<CourseGroupsPage />}
                        />
                        <Route
                          path={AppRoutesPaths.COURSES_USERS_ROUTE}
                          element={
                            <CourseUsersPage type={MaterialType.COURSE} />
                          }
                        />
                        <Route
                          path={AppRoutesPaths.COURSES_SCORM_PREVIEW_ROUTE}
                          element={<ContentPreviewPage />}
                        />
                        <Route
                          path={AppRoutesPaths.COURSES_ANALYTICS_ROUTE}
                          element={<CoursesAnalytics />}
                        />
                        <Route
                          path={AppRoutesPaths.COURSES_RESULTS_ROUTE}
                          element={<CoursesResultsPage />}
                        />
                        <Route
                          path={AppRoutesPaths.COURSES_TRANSLATE_ROUTE}
                          element={<CourseTranslatePage />}
                        />
                        <Route
                          path={AppRoutesPaths.COURSES_CUSTOMIZE_ROUTE}
                          element={<CustomizeCoursePage />}
                        />
                        <Route
                          path={AppRoutesPaths.COURSES_CHANGES_ROUTE}
                          element={<CourseChangesPage />}
                        />
                        <Route
                          path={
                            AppRoutesPaths.COURSES_REPLACE_SCHEDULED_CONTENT_ROUTE
                          }
                          element={<ReplaceScheduledContentPage />}
                        />
                      </>
                    )}
                    {can(Actions.READ, Subjects.COURSE_ACCOUNT_SCHEDULES) && (
                      <Route
                        path={AppRoutesPaths.ACCOUNT_COURSE_EVENT_LOG}
                        element={<AccountCourseSchedulePage />}
                      />
                    )}
                    {can(Actions.READ, Subjects.COURSE_PLANNER) && (
                      <Route
                        path={AppRoutesPaths.ACCOUNT_COURSE_PLANNER_ROUTE}
                        element={<AccountTrainingPlannerPage />}
                      />
                    )}

                    {/* Resources */}
                    {can(Actions.CREATE, Subjects.COURSES) && (
                      <Route
                        path={AppRoutesPaths.RESOURCES_CREATE_ROUTE}
                        element={
                          <MaterialCreatePage type={MaterialType.RESOURCE} />
                        }
                      />
                    )}
                    {can(Actions.READ, Subjects.COURSES) && (
                      <>
                        <Route
                          path={AppRoutesPaths.RESOURCES_ROUTE}
                          element={
                            <MaterialsPage type={MaterialType.RESOURCE} />
                          }
                        />
                        <Route
                          path={AppRoutesPaths.RESOURCES_EDIT_ROUTE}
                          element={
                            <MaterialEditPage type={MaterialType.RESOURCE} />
                          }
                        />
                        <Route
                          path={AppRoutesPaths.RESOURCES_SCHEDULE_ROUTE}
                          element={<CourseSchedulePage isMaterial />}
                        />
                        <Route
                          path={AppRoutesPaths.RESOURCES_ACCOUNTS_ROUTE}
                          element={
                            <CourseAccountsPage type={MaterialType.RESOURCE} />
                          }
                        />
                        <Route
                          path={AppRoutesPaths.RESOURCES_USERS_ROUTE}
                          element={
                            <CourseUsersPage type={MaterialType.RESOURCE} />
                          }
                        />
                      </>
                    )}

                    {/* Posters */}
                    {can(Actions.CREATE, Subjects.COURSES) && (
                      <Route
                        path={AppRoutesPaths.POSTER_CREATE_ROUTE}
                        element={
                          <MaterialCreatePage type={MaterialType.POSTER} />
                        }
                      />
                    )}
                    {can(Actions.READ, Subjects.COURSES) && (
                      <>
                        <Route
                          path={AppRoutesPaths.POSTERS_ROUTE}
                          element={<PostersPage />}
                        />
                        <Route
                          path={AppRoutesPaths.POSTER_EDIT_ROUTE}
                          element={
                            <MaterialEditPage type={MaterialType.POSTER} />
                          }
                        />
                        <Route
                          path={AppRoutesPaths.POSTER_ACCOUNTS_ROUTE}
                          element={
                            <CourseAccountsPage type={MaterialType.POSTER} />
                          }
                        />
                      </>
                    )}

                    {/* Custom materials */}
                    {!currentAccount?.isSystem && (
                      <>
                        {can(Actions.CREATE, Subjects.COURSES) &&
                          !isBranchAdmin && (
                            <Route
                              path={
                                AppRoutesPaths.CUSTOM_MATERIALS_CREATE_ROUTE
                              }
                              element={
                                <MaterialCreatePage
                                  type={MaterialType.CUSTOM_MATERIAL}
                                />
                              }
                            />
                          )}
                        {can(Actions.READ, Subjects.COURSES) && (
                          <>
                            <Route
                              path={AppRoutesPaths.CUSTOM_MATERIALS_ROUTE}
                              element={<OwnMaterialsTransitionPage />}
                            />
                            <Route
                              path={AppRoutesPaths.CUSTOM_MATERIALS_EDIT_ROUTE}
                              element={<CustomMaterialEditRedirect />}
                            />
                            <Route
                              path={
                                AppRoutesPaths.CUSTOM_MATERIALS_SCHEDULE_ROUTE
                              }
                              element={<CourseSchedulePage isMaterial />}
                            />
                            <Route
                              path={AppRoutesPaths.CUSTOM_MATERIALS_USERS_ROUTE}
                              element={
                                <CourseUsersPage
                                  type={MaterialType.CUSTOM_MATERIAL}
                                />
                              }
                            />
                            <Route
                              path={
                                AppRoutesPaths.CUSTOM_MATERIALS_PREVIEW_ROUTE
                              }
                              element={<DocumentViewPage isPreview />}
                            />
                          </>
                        )}
                      </>
                    )}
                  </>
                }

                {/* USER PROFILE */}
                {can(Actions.UPDATE, Subjects.ME) && (
                  <Route
                    path={AppRoutesPaths.PROFILE_SETTINGS_ROUTE}
                    element={<ProfileSettingsPage />}
                  />
                )}
                {/* Reports */}
                {can(Actions.READ, Subjects.ACCOUNT_REPORTS) && (
                  <>
                    <Route
                      path={AppRoutesPaths.REPORTS_SUMMARY_ROUTE}
                      element={<SummaryReportsPage />}
                    />
                    <Route
                      path={AppRoutesPaths.REPORTS_AWARENESS_ROUTE}
                      element={<AwarenessReportsPage />}
                    />
                  </>
                )}

                {can(Actions.READ, Subjects.FEATURE_FLAGS) && (
                  <Route
                    path={AppRoutesPaths.FEATURES_ROUTE}
                    element={<FeaturesPage />}
                  />
                )}
              </Route>

              {/* Learners Dashboard Routes */}
              {
                <Route element={<AppDashboardTypeSetter isAdmin={false} />}>
                  <Route
                    path={AppRoutesPaths.LEARNER_DASHBOARD_ACTIVE_ROUTE}
                    element={<LearnerDashboardActivePage />}
                  />
                  <Route
                    path={AppRoutesPaths.LEARNER_DASHBOARD_COMPLETED_ROUTE}
                    element={<LearnerDashboardCompletedPage />}
                  />
                  <Route
                    path={AppRoutesPaths.LEARNER_PHISHING_ROUTE}
                    element={<LearnerPhishingOverviewPage />}
                  />
                  <Route
                    path={AppRoutesPaths.LEARNER_PHISHING_DETAIL_ROUTE}
                    element={<LearnerPhishingDetailPage />}
                  />
                  <Route
                    path={AppRoutesPaths.COURSE_INFO_ROUTE}
                    element={<ScormCourseInfoPage />}
                  />
                  <Route
                    path={AppRoutesPaths.CONTENT_INFO_ROUTE}
                    element={<ScormCourseInfoPage />}
                  />
                  <Route
                    path={AppRoutesPaths.COURSE_PLAY_ROUTE}
                    element={<ContentPlayerPage />}
                  />
                  <Route
                    path={AppRoutesPaths.CONTENT_PLAY_ROUTE}
                    element={<ContentPlayerPage />}
                  />
                  <Route
                    path={AppRoutesPaths.DOCUMENT_VIEW_ROUTE}
                    element={<DocumentViewPage />}
                  />
                  {can(Actions.UPDATE, Subjects.ME) && (
                    <Route
                      path={AppRoutesPaths.LEARNER_PROFILE_SETTINGS_ROUTE}
                      element={<ProfileSettingsPage />}
                    />
                  )}
                </Route>
              }

              {/* Catch all unmatched routes */}
              <Route path="*" element={<NotFoundPage />} />
            </Route>
          </Route>
        </Route>
      </Route>
      <Route path={AppRoutesPaths.LOGIN_ROUTE} element={<LoginPage />} />
      <Route
        path={AppRoutesPaths.TAKE_SURVEY_ROUTE}
        element={<TakeSurveyPage />}
      />
      <Route
        path={AppRoutesPaths.UNAUTHORIZED_ROUTE}
        element={<AppUnauthorized />}
      />
      <Route
        path={AppRoutesPaths.COMPLETE_KEYCLOAK_SIGN_IN_ROUTE}
        element={<CompleteSignIn />}
      />
      <Route path={AppRoutesPaths.LOGOUT} element={<LogoutPage />} />
    </Routes>
  );
};

export default AppRouter;
